import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { getMeApi } from '@/api/login'
import { schedulePrefetchRoutes } from '@/utils/routePrefetch'
import { showVersionUpdateConfirm } from '@/hooks/web/useVersionCheck'

const joinRoutePath = (parentPath: string, routePath: string): string => {
  if (!routePath) return parentPath || '/'
  if (routePath.startsWith('/')) return routePath
  return `${parentPath.replace(/\/$/, '')}/${routePath}`.replace(/\/+/g, '/')
}

const findFirstAccessiblePath = (routes: AppRouteRecordRaw[], parentPath = ''): string => {
  for (const route of routes) {
    const path = String(route.path || '')
    if (route.meta?.hidden || path.includes(':path')) continue

    const fullPath = joinRoutePath(parentPath, path)
    if (route.children?.length) {
      const childPath = findFirstAccessiblePath(route.children, fullPath)
      if (childPath) return childPath
    }

    if (route.redirect && typeof route.redirect === 'string') {
      return route.redirect
    }

    if (fullPath && fullPath !== '/404') {
      return fullPath
    }
  }

  return ''
}

const { start, done } = useNProgress()

// 跳转反馈只保留顶部 NProgress 进度条;不再用 pageLoading 全屏蒙层盖内容区,
// 让目标页筛选表单立即可见,列表区域由页面自身的局部 loading 负责。
router.beforeEach(async (to, _from, next) => {
  start()
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()
  if (userStore.getUserInfo) {
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      if (permissionStore.getIsAddRouters) {
        if (to.path === '/') {
          next({
            path: findFirstAccessiblePath(permissionStore.getAddRouters) || '/404',
            replace: true
          })
          return
        }

        // 动态路由已加载过，但仍需检查当前路由是否存在（刷新后动态路由丢失的情况）
        const routeExists = to.matched.some((route) => route.name !== 'NotFoundFallback')
        if (routeExists) {
          next()
          return
        }
        // 路由不存在，重新加载动态路由
        permissionStore.setIsAddRouters(false)
      }

      if (!permissionStore.getIsAddRouters) {
        // F5 浏览器后 permission store 不持久化 → 走到这里。
        // 主动调 /admin/me 拿最新菜单 — 这样老板在后台改了菜单/权限/角色绑定，用户 F5 即可生效。
        // 登录后首次跳转不会走到这（LoginForm 已 setIsAddRouters(true) 走 fast path）。
        // me 失败时（网络断/token 过期）fallback 用 userStore 里的旧菜单，让用户至少能用；token 过期由 axios 拦截器跳登录页。
        try {
          const meRes = await getMeApi()
          if (meRes?.data?.user) {
            userStore.setUserInfo(meRes.data.user as any)
            userStore.setRoleRouters(((meRes.data.user as any).menus ?? []) as any)
          }
        } catch (e) {
          console.warn('[router] /admin/me 失败，fallback 用旧菜单', e)
        }

        const roleRouters = userStore.getRoleRouters || []

        if (appStore.getDynamicRouter) {
          if (roleRouters && roleRouters.length > 0) {
            appStore.serverDynamicRouter
              ? await permissionStore.generateRoutes(
                  'server',
                  roleRouters as AppCustomRouteRecordRaw[]
                )
              : await permissionStore.generateRoutes('frontEnd', roleRouters as string[])
          } else {
            await permissionStore.generateRoutes('static')
          }
        } else {
          await permissionStore.generateRoutes('static')
        }

        permissionStore.getAddRouters.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw)
        })
        permissionStore.setIsAddRouters(true)
      }

      if (to.path === '/') {
        next({
          path: findFirstAccessiblePath(permissionStore.getAddRouters) || '/404',
          replace: true
        })
        return
      }

      next({ path: to.path, query: to.query, hash: to.hash, replace: true })
    }
  } else {
    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  // 动态路由就绪后,趁浏览器空闲预取菜单页面的懒加载 chunk(整个会话只跑一次),
  // 让没去过的菜单也能秒切;登录首跳和 F5 重建路由两条路径都会经过这里。
  const permissionStore = usePermissionStoreWithOut()
  if (permissionStore.getIsAddRouters) {
    schedulePrefetchRoutes(permissionStore.addRouters)
  }
})

// 发版后旧 chunk 文件 404（Vite 重新打包后 hash 变了、旧文件名的 chunk 被删）→ 懒加载页面
// 动态 import 失败，路由导航挂起、进度条走不动、必须手动刷新。
// 这里兜底：捕获「动态 import 失败」类错误时自动刷新加载新版本，用户无感。
// 防无限刷新：同一会话 10s 内只自动刷一次，刷完还失败就不再刷（避免旧 chunk 真没了时死循环）。
router.onError((error) => {
  const msg = (error as { message?: string })?.message || ''
  const isChunkLoadError =
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unable to preload CSS')
  if (!isChunkLoadError) return

  // 导航失败时 afterEach 不会执行，进度条会卡住，先手动收掉
  done()

  const RELOAD_KEY = '__chunk_reload_at__'
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
  const now = Date.now()
  if (now - last > 10000) {
    sessionStorage.setItem(RELOAD_KEY, String(now))
    window.location.reload()
  } else {
    // 10s 内已自动刷过一次仍失败：不再无感自刷（防死循环），改为弹窗让用户决定是否再刷新
    showVersionUpdateConfirm('页面资源已更新，需要刷新后继续使用。')
  }
})
